import React, { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { FRIENDLY_CAPTCHA_ENDPOINT } from '@/integrations/captcha/constants';
import { useCaptchaHook } from '@aacn.eu/use-friendly-captcha';
import { CheckIcon } from '@heroicons/react/20/solid';
import MailOpenIcon from '../../../../../public/images/mail-open.svg';
import Button from '../../shared/Button';

export type ActivationCodeSentFormData = {
  deviceId: string;
  language?: string;
  firstName: string;
  lastName: string;
  email: string;
  privacyPolicy: boolean;
};

export type ActivationCodeSentProps = {
  formData: ActivationCodeSentFormData;
};

export function ActivationCodeSent({ formData }: ActivationCodeSentProps) {
  const t = useTranslations('NukiPremiumActivationCodeSent');
  const { email, language } = formData;
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);

  const captchaHook = useCaptchaHook({
    siteKey: process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_SITE_KEY || '',
    endpoint: FRIENDLY_CAPTCHA_ENDPOINT,
    startMode: 'auto',
    language: language as any,
    showAttribution: true,
  });

  const onSubmitResend = async () => {
    try {
      const response = await fetch('/api/subscriptions/nuki-premium/activation-code', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          frcCaptchaSolution: captchaHook.captchaStatus.solution as string,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(t('errorUnknownError'));
      }

      setIsSubmissionSuccess(true);
    } catch (error: any) {
      captchaHook.resetWidget();
    }
  };

  return (
    <div className="flex justify-center">
      <div className="max-w-[56rem] overflow-y-hidden px-6">
        <div className="mb-8 flex justify-center">
          <Image
            src={MailOpenIcon}
            alt=""
            width={32}
            height={32}
            className="h-14 w-14 sm:h-20 sm:w-20"
          />
        </div>
        <div className="mb-8 text-center sm:mb-12">
          <h1 className="mb-8 text-xl font-bold sm:text-4xl">
            {t('titleCodeSent')}
            <span className="text-primary-100">!</span>
          </h1>
          <p className="sm:text-lg">
            {t.rich('messageCodeSent', {
              bold: (chunks) => <span className="font-bold">{chunks}</span>,
              email,
            })}
            <br />
            {t('checkYourInbox')}
          </p>
        </div>
        <div className="rounded-md bg-gray-100 p-5 sm:mb-8 sm:text-md">
          <p className="mb-2 font-bold">{t('listTodoTitle')}</p>
          <ul className="list-inside list-decimal leading-6">
            <li>{t('listTodoCheckEmail')}</li>
            <li>{t('listTodoFindCode')}</li>
            <li>{t('listTodoEnterCode')}</li>
            <li>{t('listTodoEnjoyBenefits')}</li>
          </ul>
        </div>
        <hr className="opacity-50" />
        <div className="my-8 text-center">
          <p className="sm:text-lg">
            {t('questionEmailNotRecieved')}
            <br />
            {t('checkTheSpam')}
          </p>
        </div>
        <div className="mb-7 flex justify-center">
          <div className="inline-block min-w-min">
            {captchaHook.CaptchaWidget({ className: 'relative' })}
          </div>
        </div>
        <div className="flex justify-center">
          {isSubmissionSuccess ? (
            <Button className="inline-flex items-center px-3 py-2" openInNewTab>
              <CheckIcon className="mr-1 inline-block w-5" />
              {t('buttonCodeSent')}
            </Button>
          ) : (
            <Button
              className="inline-flex items-center px-3 py-2"
              openInNewTab
              onClick={onSubmitResend}
            >
              {t('buttonResendCode')}
            </Button>
          )}
        </div>
        <div className="mt-8 text-center">
          <p className="sm:text-lg">
            {t('questionNeedHelp')}{' '}
            <Link className="underline" href={t('helpCenterUrl')}>
              {t('contactSupportTeam')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
